/**
 * Where a login is allowed to send you afterwards.
 *
 * ── THE BUG THIS EXISTS TO PREVENT ──
 * An unchecked `?next=` on a login page is a textbook open redirect: an
 * attacker sends a link to Fint's own login page, the user signs in
 * legitimately - correct domain, correct padlock, real session - and lands on a
 * page the attacker chose. Every instinct a person has been taught for spotting
 * a phishing link says the link was fine, because it was.
 *
 * ── WHY AN ALLOW-LIST AND NOT "STARTS WITH A SLASH" ──
 * `//evil.example` starts with a slash. A browser reads it as a
 * protocol-relative URL and navigates off-site, and `startsWith('/')` waves it
 * through. So does `/\evil.example` in some browsers, and so does anything a
 * `new URL()` with a base resolves to another origin. The rule here is
 * therefore not "is this relative" but "is this one of a handful of paths we
 * named": a match against known SPA routes, with the query string kept only for
 * the routes that need one.
 *
 * It is deliberately small. `next` exists for exactly one flow today - the
 * OAuth consent screen sending a signed-out user through login and back - and a
 * list that grows only when a feature needs it is a list that cannot quietly
 * become "anything".
 */

/** The routes a `next` may name. Add one only with a reason. */
const ALLOWED = [
  // The OAuth consent screen. `request` is an opaque id minted server-side by
  // /v1/oauth/authorize; it names a validated request and grants nothing.
  { path: '/oauth/consent', params: ['request'] },
] as const;

/**
 * The path to navigate to after signing in, or `undefined` if the parameter is
 * absent, malformed, or names anywhere else.
 *
 * `undefined` means "go to the dashboard", which is the safe default and the
 * behaviour that existed before `next` did.
 */
export function safeNextPath(next: string | null | undefined): string | undefined {
  if (!next) return undefined;

  // Parsed against a fixed dummy origin rather than `window.location`, so an
  // absolute URL in `next` resolves to THAT origin and fails the check below
  // instead of quietly resolving to a real page.
  let url: URL;
  try {
    url = new URL(next, 'https://next.invalid');
  } catch {
    return undefined;
  }

  // Anything that escaped to another origin - `https://evil.example`,
  // `//evil.example`, a backslash trick - is now not on next.invalid.
  if (url.origin !== 'https://next.invalid') return undefined;

  const route = ALLOWED.find((candidate) => candidate.path === url.pathname);
  if (!route) return undefined;

  const kept = new URLSearchParams();
  for (const name of route.params) {
    const value = url.searchParams.get(name);
    if (value) kept.set(name, value);
  }
  const query = kept.toString();
  return query ? `${route.path}?${query}` : route.path;
}
